import { z } from 'zod';

export const aiResponseModeSchema = z.enum([
  'standard',
  'evidence',
  'red_team',
  'executive_memo',
  'technical_review',
  'legal_compliance',
]);
export type AiResponseMode = z.infer<typeof aiResponseModeSchema>;

export const aiPromptTemplateSchema = z.enum([
  'anti_hallucination_check',
  'red_team_review',
  'ceo_reality_check',
  'evidence_based_recommendation',
  'legal_compliance_review',
  'technical_architecture_review',
  'bias_detection',
  'executive_decision_matrix',
]);
export type AiPromptTemplate = z.infer<typeof aiPromptTemplateSchema>;

export const AI_RESPONSE_SECTION_LABELS = [
  'Facts',
  'Assumptions',
  'Unknowns',
  'Confidence score',
  'Evidence/citations',
  'Risks',
  'Counterarguments',
  'Recommendation',
  'Tradeoffs',
  'What would change the recommendation',
] as const;

export const AI_RESPONSE_MODE_OPTIONS: readonly {
  value: AiResponseMode;
  label: string;
  uiLabel: string;
  description: string;
  templates: readonly AiPromptTemplate[];
}[] = [
  {
    value: 'standard',
    label: 'Standard answer',
    uiLabel: 'Standard',
    description: 'Clear answer with required uncertainty and recommendation sections.',
    templates: ['anti_hallucination_check'],
  },
  {
    value: 'evidence',
    label: 'Evidence-based answer',
    uiLabel: 'Accuracy Mode',
    description: 'Requires cited facts, missing-evidence acknowledgement, and confidence.',
    templates: ['anti_hallucination_check', 'evidence_based_recommendation'],
  },
  {
    value: 'red_team',
    label: 'Hostile red-team review',
    uiLabel: 'Red Team Mode',
    description: 'Stress-tests the idea and prioritizes failure modes over agreement.',
    templates: ['anti_hallucination_check', 'red_team_review', 'bias_detection'],
  },
  {
    value: 'executive_memo',
    label: 'Executive decision memo',
    uiLabel: 'CEO Review Mode',
    description: 'Frames decisions as options, risks, tradeoffs, and a recommendation.',
    templates: ['anti_hallucination_check', 'ceo_reality_check', 'executive_decision_matrix'],
  },
  {
    value: 'technical_review',
    label: 'Technical review',
    uiLabel: 'Technical Review Mode',
    description: 'Reviews architecture, correctness, maintainability, security, and operations.',
    templates: ['anti_hallucination_check', 'technical_architecture_review'],
  },
  {
    value: 'legal_compliance',
    label: 'Legal/compliance risk review',
    uiLabel: 'Legal Risk Review Mode',
    description:
      'Separates legal risk signals from non-lawyer recommendations and escalation needs.',
    templates: ['anti_hallucination_check', 'legal_compliance_review'],
  },
] as const;

export const AI_PROMPT_TEMPLATES: Record<
  AiPromptTemplate,
  { title: string; system: string; developer: string }
> = {
  anti_hallucination_check: {
    title: 'Anti-hallucination check',
    system:
      'You must separate verified facts, assumptions, unknowns, risks, counterarguments, and recommendations. If evidence is missing, say so plainly.',
    developer:
      'Do not invent facts. Do not present opinion as verified. Include confidence and what evidence would change the recommendation.',
  },
  red_team_review: {
    title: 'Red-team review',
    system:
      'Act as a hostile but fair reviewer. Prioritize failure modes, missing evidence, false certainty, and downside risk.',
    developer:
      'Challenge weak assumptions. List risks and counterarguments before the recommendation. Do not soften material problems.',
  },
  ceo_reality_check: {
    title: 'CEO reality check',
    system:
      'Evaluate the decision as an executive operator. Separate what is known from what is merely desired.',
    developer:
      'Surface cost, sequencing, reversibility, customer impact, and the evidence needed to make a stronger call.',
  },
  evidence_based_recommendation: {
    title: 'Evidence-based recommendation',
    system:
      'Only treat claims as facts when evidence is supplied. Missing evidence must be listed as an unknown.',
    developer:
      'Cite evidence when available, assign confidence, include tradeoffs, and state what would change the recommendation.',
  },
  legal_compliance_review: {
    title: 'Legal/compliance review',
    system:
      'Review for legal and compliance risk without claiming to be counsel. Separate legal facts from operational opinions.',
    developer:
      'Identify missing policy, contract, consent, privacy, billing, retention, and escalation evidence. Recommend counsel review when stakes require it.',
  },
  technical_architecture_review: {
    title: 'Technical architecture review',
    system:
      'Review technical decisions for correctness, reliability, maintainability, security, scalability, and testability.',
    developer:
      'Call out architectural assumptions, operational risks, missing tests, migration risks, and evidence that would change the design.',
  },
  bias_detection: {
    title: 'Bias detection',
    system:
      'Inspect the response for one-sided framing, flattering agreement, omitted stakeholders, and unsupported certainty.',
    developer:
      'List bias risks, counterarguments, excluded populations, missing data, and safer alternate interpretations.',
  },
  executive_decision_matrix: {
    title: 'Executive decision matrix',
    system:
      'Compare decision options using explicit criteria, evidence strength, risks, reversibility, and recommendation confidence.',
    developer:
      'Include tradeoffs for each option and state the evidence that would change the preferred option.',
  },
};

const UNRESOLVED_PLACEHOLDER_PATTERN =
  /\b(TODO|TBD|FIXME)\b|lorem ipsum|\[citation needed\]|\{\{[^}]+\}\}/i;

const qualityTextSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => !UNRESOLVED_PLACEHOLDER_PATTERN.test(value), {
    message: 'Unresolved placeholder text is not allowed',
  });

export const aiEvidenceSchema = z
  .object({
    label: qualityTextSchema,
    citation: qualityTextSchema.optional(),
    url: z.string().trim().url().optional(),
  })
  .refine((item) => item.citation != null || item.url != null, {
    message: 'Evidence items must include a citation or URL',
  });
export type AiEvidence = z.infer<typeof aiEvidenceSchema>;

export const aiRecommendationSchema = z.object({
  summary: qualityTextSchema,
  rationale: qualityTextSchema,
  tradeoffs: z.array(qualityTextSchema).min(1),
});
export type AiRecommendation = z.infer<typeof aiRecommendationSchema>;

export const aiQualityResponseSchema = z.object({
  mode: aiResponseModeSchema.default('standard'),
  facts: z.array(qualityTextSchema).min(1),
  assumptions: z.array(qualityTextSchema).min(1),
  unknowns: z.array(qualityTextSchema).min(1),
  confidenceScore: z.number().min(0).max(1),
  evidence: z.array(aiEvidenceSchema),
  risks: z.array(qualityTextSchema).min(1),
  counterarguments: z.array(qualityTextSchema).min(1),
  recommendation: aiRecommendationSchema,
  whatWouldChangeRecommendation: z.array(qualityTextSchema).min(1),
});
export type AiQualityResponse = z.infer<typeof aiQualityResponseSchema>;

export type AiQualityResponseInput = z.input<typeof aiQualityResponseSchema>;

const MISSING_EVIDENCE_UNKNOWN =
  'Evidence is missing or incomplete; unsupported claims should be treated as unverified.';

function uniqueItems(items: string[]): string[] {
  return [...new Set(items.map((item) => item.trim()).filter(Boolean))];
}

export function normalizeAiQualityResponse(input: AiQualityResponseInput): AiQualityResponse {
  const evidence = input.evidence ?? [];
  const unknowns = uniqueItems(input.unknowns ?? []);

  if (evidence.length === 0 && !unknowns.some((item) => item.toLowerCase().includes('evidence'))) {
    unknowns.unshift(MISSING_EVIDENCE_UNKNOWN);
  }

  return aiQualityResponseSchema.parse({
    ...input,
    unknowns,
    evidence,
  });
}

export function buildAiResponseQualityPrompt(mode: AiResponseMode = 'standard'): {
  mode: AiResponseMode;
  templates: readonly AiPromptTemplate[];
  system: string;
  developer: string;
} {
  const option = AI_RESPONSE_MODE_OPTIONS.find((item) => item.value === mode);
  const templates = option?.templates ?? ['anti_hallucination_check'];
  const requiredSections = AI_RESPONSE_SECTION_LABELS.join(', ');
  const selectedTemplates = templates.map((templateId) => AI_PROMPT_TEMPLATES[templateId]);

  return {
    mode,
    templates,
    system: selectedTemplates.map((template) => template.system).join('\n\n'),
    developer: `${selectedTemplates
      .map((template) => template.developer)
      .join('\n\n')} Return every response with these sections: ${requiredSections}.`,
  };
}

function renderList(items: readonly string[]): string {
  return items.map((item) => `- ${item}`).join('\n');
}

function renderEvidence(items: readonly AiEvidence[]): string {
  if (items.length === 0) {
    return '- No evidence or citations were supplied; treat factual claims as unverified.';
  }

  return items
    .map((item) => {
      const source = item.url ?? item.citation;
      return source ? `- ${item.label}: ${source}` : `- ${item.label}`;
    })
    .join('\n');
}

export function renderAiQualityResponse(response: AiQualityResponseInput): string {
  const parsed = normalizeAiQualityResponse(response);
  const confidencePercent = Math.round(parsed.confidenceScore * 100);

  return [
    `## Facts\n${renderList(parsed.facts)}`,
    `## Assumptions\n${renderList(parsed.assumptions)}`,
    `## Unknowns\n${renderList(parsed.unknowns)}`,
    `## Confidence score\n${confidencePercent}%`,
    `## Evidence/citations\n${renderEvidence(parsed.evidence)}`,
    `## Risks\n${renderList(parsed.risks)}`,
    `## Counterarguments\n${renderList(parsed.counterarguments)}`,
    [
      '## Recommendation',
      parsed.recommendation.summary,
      '',
      'Rationale:',
      parsed.recommendation.rationale,
    ].join('\n'),
    `## Tradeoffs\n${renderList(parsed.recommendation.tradeoffs)}`,
    `## What would change the recommendation\n${renderList(parsed.whatWouldChangeRecommendation)}`,
  ].join('\n\n');
}
